import {
  AutoResponseMessage,
  HighPrioritySystemMessage,
  LowPrioritySystemMessage,
  MESSAGE_TYPES,
} from '@zg-rentals/satellite-components';
import { List } from '@zillow/constellation-10';
import { memo, useCallback } from 'react';
import type { MessageList_GetConversationQuery } from '../../types/GraphqlTypes';
import { SatelliteAiAutoResponseType } from '../../types/GraphqlTypes';
import type { TriggerLocation } from '../../constants/clickstreamEvents/globals';
import type {
  ConversationIdentity,
  HandleOpenDeleteAttachmentsModal,
  HandleOpenImagesViewer,
  OnResendMessageComplete,
} from '../MessageListItems';
import ApplicationRequest from './components/ApplicationRequest';
import ListingMessage from './components/ListingMessage';
import PhoneLead from './components/PhoneLead';

const { LISTING_MESSAGE, PHONE_LEAD, SYSTEM_MESSAGE } = MESSAGE_TYPES;
const { APPLICATION_REQUEST } = SYSTEM_MESSAGE.APPLICATIONS;

export type MessageListMessage = ArrayElement<
  MessageList_GetConversationQuery['ZRM_getConversationById']['conversation']
>;

export type OnSystemMessageCtaClickArgs = {
  messageType: string;
  url: string;
};

export type OnSystemMessageCtaClick = (args: OnSystemMessageCtaClickArgs) => void;

type MessageItemProps = {
  conversation: ConversationIdentity;
  onResendMessageComplete?: OnResendMessageComplete;
  isLastMessageInList: boolean;
  message: MessageListMessage;
  onSystemMessageCtaClick: OnSystemMessageCtaClick;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
  triggerLocation: TriggerLocation;
};

const MessageItem = ({
  conversation,
  onResendMessageComplete,
  isLastMessageInList,
  message: messageObject,
  onSystemMessageCtaClick,
  openDeleteAttachmentsModal,
  openImagesViewer,
  triggerLocation,
}: MessageItemProps): JSX.Element => {
  const { conversationId, listingAlias = '' } = conversation;
  const {
    aiAutoResponseType,
    attachments,
    isMessageOwner,
    message: messageContent,
    messageDateMs,
    messageLink,
    messageType,
    senderName,
    title,
    uiTreatmentType,
  } = messageObject;

  const ctaText = messageLink?.text ?? '';
  const systemMessageUrl = messageLink?.url ?? '';
  const isHighPrioritySystemMessage =
    uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY || uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY_WITH_LOGO;
  const shouldDisplaySentStatus = !!messageDateMs && isLastMessageInList && !!isMessageOwner;

  const handleSystemMessageCtaClick = useCallback(() => {
    onSystemMessageCtaClick({ messageType, url: systemMessageUrl });
  }, [onSystemMessageCtaClick, messageType, systemMessageUrl]);

  let content: JSX.Element;
  if (aiAutoResponseType === SatelliteAiAutoResponseType.EliseAi && messageContent) {
    content = <AutoResponseMessage message={messageContent} messageDateMs={messageDateMs} metaLabelType="eliseAI" />;
  } else if (aiAutoResponseType === SatelliteAiAutoResponseType.ValAi && messageContent) {
    content = <AutoResponseMessage message={messageContent} messageDateMs={messageDateMs} metaLabelType="valAI" />;
  } else {
    switch (messageType) {
      case LISTING_MESSAGE:
        content = (
          <ListingMessage
            attachments={attachments}
            conversation={conversation}
            onResendMessageComplete={onResendMessageComplete}
            isMessageOwner={isMessageOwner ?? false}
            messageContent={messageContent ?? ''}
            messageDateMs={messageDateMs}
            senderName={senderName ?? ''}
            shouldDisplaySentStatus={shouldDisplaySentStatus}
            messageId={messageObject.messageId ?? ''}
            uiTreatmentType={uiTreatmentType ?? ''}
            openDeleteAttachmentsModal={openDeleteAttachmentsModal}
            openImagesViewer={openImagesViewer}
            triggerLocation={triggerLocation}
          />
        );
        break;
      case PHONE_LEAD:
        content = (
          <PhoneLead
            isMessageOwner={isMessageOwner ?? false}
            messageContent={messageContent ?? ''}
            messageDateMs={messageDateMs}
          />
        );
        break;
      case APPLICATION_REQUEST:
        content = (
          <ApplicationRequest
            conversationId={conversationId}
            listingAlias={listingAlias}
            messageObject={messageObject}
          />
        );
        break;
      default:
        content = isHighPrioritySystemMessage ? (
          <HighPrioritySystemMessage
            ctaText={ctaText}
            message={messageContent ?? ''}
            messageDateMs={messageDateMs}
            messageType={messageType}
            onCtaClick={handleSystemMessageCtaClick}
            title={title ?? ''}
            uiTreatmentType={uiTreatmentType ?? ''}
          />
        ) : (
          <LowPrioritySystemMessage
            ctaText={ctaText}
            message={messageContent ?? ''}
            messageDateMs={messageDateMs}
            url={systemMessageUrl}
          />
        );
    }
  }

  return (
    <List.Item
      aria-live={isLastMessageInList ? 'polite' : 'off'}
      aria-roledescription="message"
      data-testid="message-item"
    >
      {content}
    </List.Item>
  );
};

export default memo(MessageItem);
